import * as actionTypes from "../constants/actionTypes";
import * as config from "../constants/config";
import * as handlers from "../constants/handlers";
import * as methods from "../constants/methods";
import * as resources from "../constants/resources";
import * as settings from "../constants/settings";
import * as windowService from "../services/windowService";
import * as messageHelper from "../helpers/messageHelper";
import * as batchActions from "../actions/batchActions";
import * as moduleActions from "../actions/moduleActions";
import * as partialUpdatesActions from "../actions/partialUpdatesActions";
import * as stockActions from "../actions/stockActions";

/**
 * @class StoreProvider
 */
export default class StoreProvider {
    /**
     * Pointer to store object
     */
    _store = null;

    _rounding = settings.DEFAULT_ROUNDING;

    _priceRounding = settings.DEFAULT_ROUNDING;

    _currencyCode = settings.DEFAULT_CURRENCY_CODE;

    _currencySymbol = settings.DEFAULT_CURRENCY_SYMBOL;

    _primaryCurrencyCode = settings.DEFAULT_CURRENCY_CODE;

    _zoom = settings.DEFAULT_ZOOM;

    _units = [];

    _customFormatNumber = null;

    _customFormatCurrency = null;

    _customFormatDateTime = null;

    _customFormatDate = null;

    _customFormatTime = null;

    _customFormulaForMasterProductDefault = null;

    _diskSpaceNotification = null;

    _formulaListOrder = null;

    /**
     * Flag for debug mode.
     */
    _debugMode = false;

    /**
     * Number of seconds the current process has been running.
     */
    _uptime = 0;

    /**
     * Number of days the current process has been running.
     */
    _uptimeDay = 0;

    /**
     * Current 'remind me later' interval ID.
     */
    _partialUpdateIntervalId = null;

    /**
     * Number of seconds since the 'remind me later' button was clicked.
     */
    _partialUpdateTime = 0;

    /**
     * Current screensaver interval ID.
     */
    _intervalId = null;

    /**
     * Screensaver active.
     */
    _activeScreenSaver = false;

    /**
     * Screensaver start time.
     */
    _screenSaverTimeout = config.DEFAULT_APP_INTERVAL;

    /**
     * Automatic logout active.
     */
    _activeLogout = false;

    /**
     * Automatic logout start time.
     */
    _logoutTimeout = config.DEFAULT_APP_INTERVAL;

    /**
     * Number of seconds since last user action.
     */
    _userIdleTime = 0;

    /**
     * Time of last server request or response.
     */
    _lastServerActivity = 0;

    /**
     * Interval for websocket ping pong.
     */
    _pingInterval = config.DEFAULT_APP_INTERVAL;

    /**
     * Timeout of websocket ping request.
     */
    _pingIntervalTimeout = config.DEFAULT_APP_INTERVAL + config.WEBSOCKET_PING_TIMEOUT;

    /**
     * @param {Object}  store
     * @param {Boolean} debugMode
     */
    constructor(store, debugMode) {
        this._store = store;
        this._debugMode = debugMode;
    }

    /**
     * @param {Object}  action
     */
    dispatchAction = (action) => {
        if (this._store !== null) {
            this._store.dispatch(action);
        }
    };

    /**
     * @returns {Number}
     */
    getRounding = () => {
        return this._rounding;
    };

    /**
     * @param   {Number}    newRounding
     */
    setRounding = (newRounding) => {
        this._rounding = newRounding;
    };

    /**
     * @returns {String}
     */
    getCurrencyCode = () => {
        return this._currencyCode;
    };

    /**
     * @returns {String}
     */
    getCurrencySymbol = () => {
        return this._currencySymbol;
    };

    /**
     * @param   {String}    newCurrencyCode
     * @param   {String}    newCurrencySymbol
     */
    setCurrencyData = (newCurrencyCode, newCurrencySymbol) => {
        this._currencyCode = newCurrencyCode;
        this._currencySymbol = newCurrencySymbol;
    };

    /**
     * @returns {String}
     */
    getPrimaryCurrencyCode = () => {
        return this._primaryCurrencyCode;
    };

    /**
     * @param   {String}    newPrimaryCurrencyCode
     */
    setPrimaryCurrencyCode = (newPrimaryCurrencyCode) => {
        this._primaryCurrencyCode = newPrimaryCurrencyCode;
    };

    /**
     * @returns {Number}
     */
    getZoom = () => {
        return this._zoom;
    };

    /**
     * @param   {Number}    newZoom
     */
    setZoom = (newZoom) => {
        this._zoom = newZoom;
    };

    /**
     * @param   {Array} units
     */
    setUnits = (units) => {
        this._units = units;
    };

    /**
     * @returns {String | null}
     */
    getCustomFormatNumber = () => {
        return this._customFormatNumber;
    };

    /**
     * @param   {String | null} customFormatNumber
     */
    setCustomFormatNumber = (customFormatNumber) => {
        this._customFormatNumber = customFormatNumber;
    };

    /**
     * @returns {String | null}
     */
    getCustomFormatCurrency = () => {
        return this._customFormatCurrency;
    };

    /**
     * @param   {String | null} customFormatCurrency
     */
    setCustomFormatCurrency = (customFormatCurrency) => {
        this._customFormatCurrency = customFormatCurrency;
    };

    /**
     * @returns {String | null}
     */
    getCustomFormatDate = () => {
        return this._customFormatDate;
    };

    /**
     * @returns {String | null}
     */
    getCustomFormatTime = () => {
        return this._customFormatTime;
    };

    /**
     * @returns {String | null}
     */
    getCustomFormatDateTime = () => {
        return this._customFormatDateTime;
    };

    /**
     * @param   {String | null} customFormatDate
     * @param   {String | null} customFormatTime
     */
    setCustomFormatDateTime = (customFormatDate, customFormatTime) => {
        this._customFormatDate = customFormatDate;
        this._customFormatTime = customFormatTime;
        this._customFormatDateTime =
            (customFormatDate === null ? "L" : customFormatDate) +
            " " +
            (customFormatTime === null ? "LTS" : customFormatTime);
    };

    /**
     * @returns {Boolean}
     */
    getCustomFormulaForMasterProductDefault = () => {
        return this._customFormulaForMasterProductDefault;
    };

    /**
     * @param   {Boolean}    customFormulaForMasterProductDefault
     */
    setCustomFormulaForMasterProductDefault = (customFormulaForMasterProductDefault) => {
        this._customFormulaForMasterProductDefault = customFormulaForMasterProductDefault;
    };

    /**
     * @param   {Number}    id
     */
    getUnitCode = (id) => {
        for (let unit of this._units) {
            if (unit.id === id) {
                return unit.code;
            }
        }

        return null;
    };

    /**
     * @param   {Boolean}   diskSpaceNotification
     */
    setDiskSpaceNotification = (diskSpaceNotification) => {
        this._diskSpaceNotification = diskSpaceNotification;
    };

    /**
     * @param   {Boolean}   blendFormulaAndCode
     * @param   {Boolean}   formulaCodePrimary
     */
    setFormulaListOrder = (blendFormulaAndCode, formulaCodePrimary) => {
        this._formulaListOrder = blendFormulaAndCode && formulaCodePrimary ? "code" : null;
    };

    /**
     * @returns {String | null}
     */
    getFormulaListOrder = () => {
        return this._formulaListOrder;
    };

    /**
     * @returns {Boolean}
     */
    isDebugMode = () => {
        return this._debugMode;
    };

    /**
     * @param   {Boolean}   debugMode
     */
    setDebugMode = (debugMode) => {
        this._debugMode = debugMode;
    };

    /**
     * @returns {Number}
     */
    getPriceRounding = () => {
        return this._priceRounding;
    };

    /**
     * @param   {Number}    screenSaverTimeout
     */
    setScreenSaverTimeout = (screenSaverTimeout) => {
        this._screenSaverTimeout = screenSaverTimeout * 60;
    };

    /**
     * @param   {Boolean}   automaticLogout
     * @param   {Number}    automaticLogoutTimeout
     */
    setAutomaticLogout = (automaticLogout, automaticLogoutTimeout) => {
        this._activeLogout = automaticLogout;
        this._logoutTimeout = automaticLogoutTimeout * 60;
    };

    /**
     * Resets time of last user activity.
     */
    resetUserIdleTime = () => {
        this._userIdleTime = 0;
    };

    /**
     * Resets time of last server request or response.
     */
    resetLastServerActivity = () => {
        this._lastServerActivity = 0;
    };

    /**
     * Sets rounding by number of decimal places of given number at parameter.
     *
     * @param   {Number}    currencyRounding
     */
    setPriceRounding = (currencyRounding) => {
        if (currencyRounding % 1 === 0) {
            this._priceRounding = 0;
        } else {
            let valueParts = currencyRounding.toString().split(".");
            this._priceRounding = valueParts.length > 0 ? valueParts[1].length : 0;
        }
    };

    /**
     * Sets all setting items provided by StoreProvider.
     *
     * @param   {Object}    data
     */
    setSettingItems = (data) => {
        this.setRounding(data.rounding);
        this.setZoom(data.zoom);
        this.setAutomaticLogout(data.automaticLogout, data.automaticLogoutTimeout);
        this.setCustomFormatNumber(data.customFormatNumber);
        this.setCustomFormatCurrency(data.customFormatCurrency);
        this.setCustomFormatDateTime(data.customFormatDate, data.customFormatTime);
        this.setCustomFormulaForMasterProductDefault(data.customFormulaForMasterProductDefault);
        this.setDiskSpaceNotification(data.diskSpaceNotification);
        this.setFormulaListOrder(data.blendFormulaAndCode, data.formulaCodePrimary);
    };

    /**
     * @returns {String}
     */
    getUptime = () => {
        if (this._uptimeDay > 0) {
            return this._uptimeDay + " days " + this._uptime + " seconds";
        }

        return this._uptime + " seconds";
    };

    /**
     * Applies repeating actions.
     */
    processTime = () => {
        this._uptime++;
        this._userIdleTime++;
        this._lastServerActivity++;

        if (this._uptime === 86400) {
            this._uptime = 0;
            this._uptimeDay++;
        }

        if (this._activeScreenSaver && this._userIdleTime === this._screenSaverTimeout) {
            this.dispatchAction({type: actionTypes.NAVIGATION_OPEN_SCREENSAVER});
        }

        if (this._activeLogout && this._userIdleTime === this._logoutTimeout) {
            this.dispatchAction({
                type: actionTypes.SERVER_REQUEST,
                payload: {
                    type: methods.METHOD_GET_ALL_USERS,
                    params: {
                        activeOnly: true
                    }
                },
                method: handlers.GET_ALL_USERS_AND_AUTOLOGIN,
                tabId: 0
            });
        }

        if (this._lastServerActivity === this._pingInterval) {
            this.dispatchAction({type: actionTypes.WEBSOCKET_PING});
        }

        if (this._lastServerActivity === this._pingIntervalTimeout) {
            const store = this._store.getState();

            if (store.server.connected && store.server.activeServer !== null) {
                this.dispatchAction({
                    type: actionTypes.SERVER_RECONNECT,
                    payload: {
                        url: store.server.activeServer.getWebSocketUrl()
                    }
                });
                this._lastServerActivity = 0;
            }
        }

        // Every 5 minutes
        if (this._uptime % 300 === 0) {
            let statistics = windowService.getProcessStatistics();

            if (statistics !== null) {
                windowService.logInfo(
                    "Percentage of CPU: " +
                        statistics.cpu +
                        "%, the amount of memory: " +
                        statistics.memory +
                        " MB, uptime: " +
                        this.getUptime() +
                        "."
                );
            }
        }

        // 60 seconds after start and every 24 hours after last check
        if (this._uptime === 60) {
            if (this._diskSpaceNotification && windowService.isInsufficientDiskSpace()) {
                this.dispatchAction(messageHelper.getWarningMessageAction("flashMessage.runningOutOfSpace"));
            }

            const store = this._store.getState();

            if (store.login.loggedIn) {
                if (
                    !store.module.setupSoftware &&
                    store.settings.colorantBatchNumberLogic !== settings.COLORANT_BATCH_NUMBER_LOGIC_DISABLED &&
                    store.login.loggedUser.canRead(resources.SETTINGS_COLORANT_BATCH_NUMBER_LOGIC)
                ) {
                    this.dispatchAction(batchActions.performInitCheck());
                }

                if (store.module.gammaSoftware) {
                    this.dispatchAction(stockActions.calculatePredictions());
                }
            }

            if (this._uptimeDay > 0) {
                this.dispatchAction(moduleActions.reloadWinterTime());
            }
        }
    };

    /**
     * Global interval for processing of basic software functions.
     *
     * @returns {Boolean}
     */
    startInterval = () => {
        if (this._store === null) {
            return false;
        }

        const store = this._store.getState();

        this._activeScreenSaver = store.settings.activeScreensaver;
        this._screenSaverTimeout = store.settings.screensaverTimeout * 60;
        this._pingInterval = windowService.getPingInterval();

        this._intervalId = setInterval(this.processTime, 1000);

        return true;
    };

    /**
     * Clears global interval on close.
     *
     * @returns {Boolean}
     */
    clearInterval = () => {
        if (this._intervalId !== null) {
            clearInterval(this._intervalId);
        }
    };

    clearPartialUpdate = () => {
        clearInterval(this._partialUpdateIntervalId);
        this._partialUpdateIntervalId = null;
        this._partialUpdateTime = 0;
        return;
    };

    partialUpdateCheck = (remindMeLaterLength) => {
        if (remindMeLaterLength <= this._partialUpdateTime) {
            this.clearPartialUpdate();
            this.dispatchAction(partialUpdatesActions.getNewPartialUpdates());
            return;
        }
        this._partialUpdateTime++;
    };

    startPartialUpdateInterval = (remindMeLaterLength) => {
        if (this._store === null) {
            return false;
        }

        this._partialUpdateIntervalId = setInterval(() => this.partialUpdateCheck(parseInt(remindMeLaterLength)), 1000);

        return true;
    };
}
